import { Component, OnDestroy, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionInsightsPanelComponent } from './session-insights-panel.component';
import { PrejoinLiveComponent } from './prejoin-live.component';
import { TrackingApiService } from '../services/tracking-api.service';
import { parseApiDate } from '../tracking-format.util';
import { visitorDevice, visitorPlace } from '../prejoin-visitor.util';
import type { PrejoinDumpWithVisitor as PrejoinDumpRow } from '../prejoin-live.models';
import type { ReportRecording } from '../tracking-demo.models';

/**
 * Dashboard "Pre-join dumps" tab: every instant-demo visitor who touched the setup form
 * but never joined the call, newest first, paginated and scoped to this panel's product
 * (GET /api/tracking/prejoin-dumps). Selecting a row opens that visit's rrweb replay and
 * click heatmap through the same {@link SessionInsightsPanelComponent} the session
 * report uses — a dump's `domUrl` + `insights` are shaped exactly like a recording's.
 *
 * A "Live now" switch shows visitors still on the form instead
 * ({@link PrejoinLiveComponent}), each watchable live.
 */
/** Past visits refresh this often, so a visitor who just left shows up by itself. */
const REFRESH_MS = 15_000;

@Component({
  selector: 'app-prejoin-dumps',
  standalone: true,
  imports: [CommonModule, SessionInsightsPanelComponent, PrejoinLiveComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="space-y-4">
      <div class="flex flex-wrap items-center gap-3">
        <div class="inline-flex rounded-lg border border-[#EAEAEA] dark:border-line p-0.5 text-xs" role="tablist">
          @for (v of views; track v.id) {
            <button type="button" role="tab" [attr.aria-selected]="view() === v.id" (click)="view.set(v.id)"
              class="rounded-md px-3 py-1 font-medium transition-colors"
              [class]="view() === v.id
                ? 'bg-indigo-600 text-white'
                : 'text-gray-500 hover:text-gray-800 dark:text-ink-soft/60 dark:hover:text-white'">
              @if (v.id === 'live') { <span class="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-rose-500 align-middle"></span> }
              {{ v.label }}
            </button>
          }
        </div>
        <span class="text-xs text-slate-400">{{ view() === 'live'
          ? 'Visitors on the setup form right now — watch any of them live.'
          : 'Visitors who touched the form but never joined — full session replay per visit.' }}</span>
      </div>

      @if (view() === 'live') {
        <app-prejoin-live></app-prejoin-live>
      } @else {

      <div class="overflow-x-auto rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1">
        <table class="min-w-full text-sm">
          <thead class="bg-slate-50 dark:bg-white/5 text-[11px] uppercase tracking-wide text-gray-500 dark:text-ink-soft/50">
            <tr>
              <th class="px-3 py-2 text-left font-medium">When</th>
              <th class="px-3 py-2 text-left font-medium">Session</th>
              <th class="px-3 py-2 text-left font-medium">Product</th>
              <th class="px-3 py-2 text-left font-medium">Location · IP</th>
              <th class="px-3 py-2 text-left font-medium">Device</th>
              <th class="px-3 py-2 text-right font-medium">Time on page</th>
              <th class="px-3 py-2 text-right font-medium">Clicks</th>
              <th class="px-3 py-2 text-right font-medium">Rage</th>
              <th class="px-3 py-2 text-right font-medium">Dead</th>
              <th class="px-3 py-2 text-left font-medium">Ended by</th>
              <th class="px-3 py-2 text-left font-medium">Upload</th>
              <th class="px-3 py-2 text-right font-medium"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-line/60">
            @for (r of rows(); track r.id) {
              <tr class="cursor-pointer hover:bg-slate-50 dark:hover:bg-line/40"
                  [ngClass]="{ 'bg-indigo-50 dark:bg-indigo-500/10': selected()?.id === r.id }"
                  (click)="select(r)">
                <td class="px-3 py-2 tabular-nums text-gray-500">{{ timeOf(r.createdAt) }}</td>
                <td class="px-3 py-2 font-mono text-[11px] text-gray-500">{{ r.sessionId }}</td>
                <td class="px-3 py-2 capitalize text-gray-600 dark:text-gray-300">{{ r.productKeyname || '—' }}</td>
                <td class="px-3 py-2">
                  <div class="text-gray-700 dark:text-gray-200">{{ visitorPlace(r.visitor) }}</div>
                  @if (r.visitor?.ip) {
                    <div class="font-mono text-[11px] text-gray-500" [title]="r.visitor?.isp || ''">{{ r.visitor?.ip }}</div>
                  }
                </td>
                <td class="px-3 py-2 text-gray-600 dark:text-gray-300">{{ visitorDevice(r.visitor) }}</td>
                <td class="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-200">{{ duration(r.durationMs) }}</td>
                <td class="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-200">{{ r.insights?.clickCount ?? 0 }}</td>
                <td class="px-3 py-2 text-right tabular-nums" [class]="(r.insights?.rageClicks ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'">{{ r.insights?.rageClicks ?? 0 }}</td>
                <td class="px-3 py-2 text-right tabular-nums" [class]="(r.insights?.deadClicks ?? 0) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'">{{ r.insights?.deadClicks ?? 0 }}</td>
                <td class="px-3 py-2"><span class="rounded px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200">{{ reason(r.finalizeReason) }}</span></td>
                <td class="px-3 py-2 text-xs" [class]="r.uploadStatus === 'complete' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'">{{ r.uploadStatus || '—' }}</td>
                <td class="px-3 py-2 text-right">
                  @if (r.domUrl) {
                    <span class="text-xs text-indigo-600 dark:text-indigo-400">{{ selected()?.id === r.id ? 'Close' : 'Replay' }}</span>
                  }
                </td>
              </tr>
            } @empty {
              <tr><td colspan="12" class="px-3 py-10 text-center text-gray-400">{{ loading() ? 'Loading…' : 'No pre-join dumps yet.' }}</td></tr>
            }
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between text-sm text-gray-500">
        <span>{{ total() }} visit(s)</span>
        <div class="flex items-center gap-2">
          <button type="button" (click)="prev()" [disabled]="page() <= 1"
            class="rounded-md border border-gray-200 dark:border-white/10 px-2 py-1 disabled:opacity-40">Prev</button>
          <span>Page {{ page() }} / {{ totalPages() }}</span>
          <button type="button" (click)="next()" [disabled]="page() >= totalPages()"
            class="rounded-md border border-gray-200 dark:border-white/10 px-2 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>

      @if (selected(); as s) {
        <div class="flex h-[72vh] min-h-0 flex-col rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
          <div class="mb-3 flex shrink-0 items-center gap-2">
            <div class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Session {{ s.sessionId }} · {{ timeOf(s.createdAt) }}</div>
            <button type="button" (click)="selected.set(null)"
              class="ml-auto rounded-md border border-[#EAEAEA] dark:border-line px-2 py-0.5 text-[11px] text-slate-500 hover:bg-slate-100 dark:text-ink-soft/70 dark:hover:bg-line">Close</button>
          </div>
          <app-session-insights-panel [recording]="asRecording(s)" />
        </div>
      }
      }
    </div>
  `,
})
export class PrejoinDumpsComponent implements OnInit, OnDestroy {
  private readonly api = inject(TrackingApiService);

  readonly rows = signal<PrejoinDumpRow[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSize = 25;
  readonly loading = signal(false);
  readonly selected = signal<PrejoinDumpRow | null>(null);
  readonly visitorPlace = visitorPlace;
  readonly visitorDevice = visitorDevice;
  readonly views = [
    { id: 'live', label: 'Live now' },
    { id: 'past', label: 'Past visits' },
  ] as const;
  readonly view = signal<'live' | 'past'>('live');

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    void this.load();
    this.refreshTimer = setInterval(() => this.autoRefresh(), REFRESH_MS);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer !== null) clearInterval(this.refreshTimer);
  }

  /** Quietly reload page 1 of Past visits, but never under an open replay. */
  private autoRefresh(): void {
    if (this.view() === 'past' && this.page() === 1 && !this.selected() && !this.loading()) {
      void this.load();
    }
  }

  totalPages(): number { return Math.max(1, Math.ceil(this.total() / this.pageSize)); }
  prev(): void { if (this.page() > 1) { this.page.update((p) => p - 1); void this.load(); } }
  next(): void { if (this.page() < this.totalPages()) { this.page.update((p) => p + 1); void this.load(); } }

  select(r: PrejoinDumpRow): void {
    this.selected.set(this.selected()?.id === r.id ? null : r);
  }

  /** The insights panel reads a recording; a dump carries the same two fields it uses. */
  asRecording(r: PrejoinDumpRow): ReportRecording {
    return { domUrl: r.domUrl, insights: r.insights, videoRenderUrl: null, renderStatus: null };
  }

  timeOf(iso?: string | null): string {
    const d = parseApiDate(iso);
    return d ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  }

  duration(ms: number | null): string {
    if (!ms || ms <= 0) return '—';
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  reason(code: string | null): string {
    switch (code) {
      case 'beacon': return 'Tab closed';
      case 'prejoin-timeout': return '10 min no join';
      case 'grace-timeout': return 'Connection lost';
      case 'shutdown': return 'Server restart';
      default: return code || '—';
    }
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.selected.set(null);
    try {
      const res = await this.api.getPrejoinDumps(this.page(), this.pageSize);
      this.rows.set(res.items || []);
      this.total.set(res.total || 0);
    } catch {
      /* handled by api */
    } finally {
      this.loading.set(false);
    }
  }
}
